import { Behavior, type CollisionEventPayload, type Entity, type EntityId, type Rect } from '@kite/engine';
import { moveClearOf } from '../placement';
import { spawnSparkles, type SparkleOptions } from '../sparkles';

/** How a collectible behaves when something touches it. Plain data, like every behavior's config. */
export interface CollectOnTouchOptions {
  /** Asset key of the sound played on pickup. A key that never loaded plays nothing. */
  readonly soundKey: string;
  /** Where the collectible may reappear. */
  readonly area: Rect;
  /** How far the fresh position must be from whatever took it. */
  readonly clearance: number;
  /** The burst thrown out at the old position. */
  readonly sparkles: SparkleOptions;
}

/**
 * Rewards a touch, then puts the collectible somewhere else.
 *
 * A behavior rather than a system: this is one entity's rule about itself, and a second collectible
 * that behaved differently would be a second behavior rather than a branch in a system. Detecting the
 * overlap is `CollisionSystem`'s job; deciding it means "collected" is this.
 *
 * Nothing is destroyed and nothing is spawned to replace it. Moving the same entity keeps the coin
 * count fixed without any bookkeeping, and a toddler reads it as the coin having run away.
 */
export class CollectOnTouch extends Behavior {
  readonly #options: CollectOnTouchOptions;

  constructor(options: CollectOnTouchOptions) {
    super();
    this.#options = options;
  }

  /**
   * Listens for overlaps naming this entity.
   *
   * Group masks already restrict the pairs to collector against collectible, so any pair naming this
   * coin is the car and the payload's other id is who took it.
   */
  override onAttach(entity: Entity): void {
    this.subscribe('collisionEnter', (event) => {
      const collectorId = otherId(event, entity.id);
      if (collectorId !== undefined) {
        this.#collect(entity, collectorId);
      }
    });
  }

  #collect(collectible: Entity, collectorId: EntityId): void {
    const { soundKey, area, clearance, sparkles } = this.#options;

    this.context.audio.play(soundKey);
    spawnSparkles(collectible.scene, collectible.transform.position, sparkles);

    const collector = collectible.scene.getEntity(collectorId);
    moveClearOf(collectible.transform.position, area, collector?.transform.position, clearance);
  }
}

/** The other half of a collision pair, or undefined when the pair does not name `entityId` at all. */
function otherId(pair: CollisionEventPayload, entityId: EntityId): EntityId | undefined {
  if (pair.firstId === entityId) {
    return pair.secondId;
  }
  if (pair.secondId === entityId) {
    return pair.firstId;
  }
  return undefined;
}
